import React from "react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { Price } from "@/components/ui/price";
import { formatNumber } from "@/lib/utils";
import { roundTo } from "../lib/columns";

/** Brand navy (--primary) and the diverging red; SVG attributes cannot read CSS variables. */
const SERIES = "#1c4587";
const NEGATIVE = "#d03b3b";
const GRID = "#e5e7eb";
const AXIS = "#6b7280";

export interface TrendPoint {
	/** Axis label, already formatted (e.g. "16/10"). */
	label: string;
	/** Tooltip title (e.g. the full date). */
	title: string;
	value: number;
	/** Extra tooltip line (e.g. "3 invoices"). */
	detail?: string;
}

const compact = (v: number) => {
	const abs = Math.abs(v);
	if (abs >= 1_000_000) return `${roundTo(v / 1_000_000, 1)}M`;
	if (abs >= 1_000) return `${roundTo(v / 1_000, 1)}K`;
	return formatNumber(roundTo(v, 0));
};

function ChartTooltip({
	active,
	payload,
	currency,
	format,
}: {
	active?: boolean;
	payload?: { payload: TrendPoint }[];
	currency?: string;
	format: "money" | "number";
}) {
	if (!active || !payload?.length) return null;
	const point = payload[0].payload;
	return (
		<div className="rounded-lg border bg-card px-3 py-2 text-sm shadow-md">
			<p className="text-xs text-muted-foreground">{point.title}</p>
			<p className="font-semibold tabular-nums">
				{format === "money" ? (
					<Price value={roundTo(point.value, 2)} currency={currency} />
				) : (
					formatNumber(roundTo(point.value, 2))
				)}
			</p>
			{point.detail && <p className="text-xs text-muted-foreground">{point.detail}</p>}
		</div>
	);
}

interface ChartProps {
	data: TrendPoint[];
	/** Accessible name; the same numbers are in the table below the chart. */
	ariaLabel: string;
	format?: "money" | "number";
	currency?: string;
	height?: number;
}

/** One series over time, as a line with a soft fill. Mirrored in Arabic so time runs right to left. */
export function TrendChart({ data, ariaLabel, format = "money", currency, height = 260 }: ChartProps) {
	const { isRTL } = useLanguage();
	return (
		<div role="img" aria-label={ariaLabel} style={{ height }} dir="ltr">
			<ResponsiveContainer width="100%" height="100%">
				<AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
					<defs>
						<linearGradient id="reportTrendFill" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={SERIES} stopOpacity={0.22} />
							<stop offset="100%" stopColor={SERIES} stopOpacity={0.02} />
						</linearGradient>
					</defs>
					<CartesianGrid stroke={GRID} vertical={false} />
					<XAxis
						dataKey="label"
						reversed={isRTL}
						tick={{ fontSize: 12, fill: AXIS }}
						tickLine={false}
						axisLine={{ stroke: GRID }}
						minTickGap={16}
					/>
					<YAxis
						orientation={isRTL ? "right" : "left"}
						tick={{ fontSize: 12, fill: AXIS }}
						tickLine={false}
						axisLine={false}
						tickFormatter={compact}
						width={48}
					/>
					<Tooltip
						cursor={{ stroke: AXIS, strokeDasharray: "3 3" }}
						content={<ChartTooltip currency={currency} format={format} />}
					/>
					<Area
						type="monotone"
						dataKey="value"
						stroke={SERIES}
						strokeWidth={2}
						fill="url(#reportTrendFill)"
						dot={data.length <= 31 ? { r: 3, fill: SERIES, strokeWidth: 0 } : false}
						activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
						isAnimationActive={false}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
}

/** Columns that can go below zero (profit/loss): navy above the baseline, red below. */
export function SignedColumns({ data, ariaLabel, format = "money", currency, height = 240 }: ChartProps) {
	const { isRTL } = useLanguage();
	return (
		<div role="img" aria-label={ariaLabel} style={{ height }} dir="ltr">
			<ResponsiveContainer width="100%" height="100%">
				<BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
					<CartesianGrid stroke={GRID} vertical={false} />
					<XAxis
						dataKey="label"
						reversed={isRTL}
						tick={{ fontSize: 12, fill: AXIS }}
						tickLine={false}
						axisLine={false}
					/>
					<YAxis
						orientation={isRTL ? "right" : "left"}
						tick={{ fontSize: 12, fill: AXIS }}
						tickLine={false}
						axisLine={false}
						tickFormatter={compact}
						width={48}
					/>
					<ReferenceLine y={0} stroke={AXIS} />
					<Tooltip
						cursor={{ fill: "rgba(28,69,135,0.06)" }}
						content={<ChartTooltip currency={currency} format={format} />}
					/>
					<Bar dataKey="value" radius={[4, 4, 4, 4]} maxBarSize={48} isAnimationActive={false}>
						{data.map(point => (
							<Cell key={point.title} fill={point.value < 0 ? NEGATIVE : SERIES} />
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
}
